import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Upload, Trash2, Search, Image as ImageIcon, Check } from "lucide-react";
import { resolveImageUrl, uploadImage } from "@/lib/siteContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Library modal — browse / upload / pick from previously uploaded images.
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   onPick: (urlOrUrls: string | string[]) => void
 *   token: admin bearer token
 *   mode: "single" | "multi"  (default "single")
 *   manage: boolean — if true, hides the "Choose" footer and shows delete only (used by the dedicated Library tab)
 */
export default function LibraryModal({ open, onClose, onPick, token, mode = "single", manage = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(new Set());
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/uploads`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(data || []);
    } catch {
      toast.error("Couldn't load library");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) { load(); setSelected(new Set()); setQ(""); } }, [open]);

  if (!open) return null;

  const filtered = items.filter((it) => {
    if (!q.trim()) return true;
    const t = q.toLowerCase();
    return (it.original_name || "").toLowerCase().includes(t) || (it.name || "").toLowerCase().includes(t);
  });

  const toggle = (url) => {
    if (mode === "single") {
      onPick(url);
      onClose();
      return;
    }
    const next = new Set(selected);
    if (next.has(url)) next.delete(url); else next.add(url);
    setSelected(next);
  };

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    toast.loading(`Uploading ${files.length}…`, { id: "libup" });
    try {
      for (const f of files) await uploadImage(token, f);
      toast.success(`Uploaded ${files.length}`, { id: "libup" });
      await load();
    } catch { toast.error("Upload failed", { id: "libup" }); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  const remove = async (name) => {
    if (!window.confirm("Delete this image? Pages that reference it will show broken images.")) return;
    try {
      await axios.delete(`${API}/admin/uploads/${encodeURIComponent(name)}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Deleted");
      await load();
    } catch { toast.error("Delete failed"); }
  };

  const migrate = async () => {
    toast.loading("Migrating local uploads to Cloudinary…", { id: "mig" });
    try {
      const { data } = await axios.post(`${API}/admin/migrate-to-cloudinary`, {}, { headers: { Authorization: `Bearer ${token}` } });
      const n = data.migrated || 0;
      if (n > 0) toast.success(`Migrated ${n} image${n > 1 ? "s" : ""} to Cloudinary`, { id: "mig" });
      else toast.success("Nothing to migrate — all images are already on Cloudinary", { id: "mig" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Migration failed", { id: "mig" });
    }
  };

  const confirmMulti = () => {
    onPick(Array.from(selected));
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] bg-cozy-brown/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="library-modal" onClick={onClose}>
      <div className="bg-cozy-bg rounded-3xl w-full max-w-6xl max-h-[90vh] flex flex-col shadow-cozy-lg border border-cozy-gold/15 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-cozy-gold/15 gap-3 flex-wrap">
          <div>
            <h2 className="font-serif text-2xl">Image <span className="font-script text-cozy-gold">Library</span></h2>
            <p className="text-xs text-cozy-brown-soft mt-1">{items.length} image{items.length !== 1 ? "s" : ""} · {manage ? "Manage your library" : "Choose images to use, or upload new ones."}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-cozy-brown-soft" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="pl-9 pr-3 py-2 rounded-full border border-cozy-gold/25 bg-white text-sm focus:outline-none focus:border-cozy-gold" />
            </div>
            <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleFiles} className="hidden" />
            <button onClick={() => fileRef.current?.click()} className="flex items-center gap-2 px-4 py-2 rounded-full bg-cozy-gold text-white hover:bg-cozy-gold-dark transition text-sm">
              <Upload className="w-4 h-4" /> Upload
            </button>
            {manage && (
              <button onClick={migrate} title="Push any remaining local images up to Cloudinary so they survive redeploys" className="flex items-center gap-2 px-4 py-2 rounded-full bg-cozy-beige hover:bg-cozy-pink text-cozy-brown transition text-sm">
                ☁ Migrate to Cloudinary
              </button>
            )}
            <button onClick={onClose} aria-label="Close" className="w-9 h-9 rounded-full bg-white hover:bg-cozy-pink/40 flex items-center justify-center transition">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="text-center text-cozy-brown-soft py-10">Loading library…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-cozy-brown-soft py-16">
              <ImageIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>{items.length === 0 ? "No images yet. Click \"Upload\" to add some." : "No matches."}</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {filtered.map((it) => {
                const isSel = selected.has(it.url);
                return (
                  <div key={it.name} className={`relative rounded-2xl overflow-hidden bg-white shadow-cozy border-2 transition cursor-pointer ${isSel ? "border-cozy-gold ring-2 ring-cozy-gold/40" : "border-cozy-gold/10 hover:border-cozy-gold/40"}`}>
                    <button
                      onClick={() => !manage && toggle(it.url)}
                      disabled={manage}
                      className="block w-full"
                      data-testid={`library-item-${it.name}`}
                    >
                      <img src={resolveImageUrl(it.thumb_url || it.url)} alt={it.original_name || ""} className="w-full aspect-square object-cover" loading="lazy" />
                      {isSel && (
                        <div className="absolute top-2 left-2 w-7 h-7 rounded-full bg-cozy-gold text-white flex items-center justify-center shadow-cozy">
                          <Check className="w-4 h-4" />
                        </div>
                      )}
                    </button>
                    <div className="p-2 text-[10px] text-cozy-brown-soft truncate" title={it.original_name}>{it.original_name || it.name}</div>
                    <button onClick={() => remove(it.name)} aria-label="Delete" className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/95 hover:bg-red-100 text-red-600 flex items-center justify-center opacity-0 group-hover:opacity-100 hover:opacity-100 transition shadow-cozy" style={{ opacity: 1 }}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {!manage && mode === "multi" && (
          <div className="border-t border-cozy-gold/15 p-4 flex items-center justify-between bg-white">
            <span className="text-sm text-cozy-brown-soft">{selected.size} selected</span>
            <button onClick={confirmMulti} disabled={selected.size === 0} className="px-6 py-2.5 rounded-full bg-cozy-gold text-white hover:bg-cozy-gold-dark transition disabled:opacity-50">
              Use Selected
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
